import BoldText from '@/components/BoldText';
import { Collapsible } from '@/components/Collapsible';
import LayoutSettings from '@/components/LayoutSetting/LayoutSettings';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import {
  COMMA_AND_SEMICOLON_SEPARATOR,
  // DELIMITER_OR_WHITESPACE_SEPARATOR,
  TRIM_SPECIAL_CHARS,
  WHITE_SPACE_AND_UNDERSCORE_REMOVER,
} from '@/constants';
import { HIDE_VALIDATE_ZIP_CODE_SHOP } from '@/constants/custom-shop.constant';
import { PostalCodeType } from '@/constants/enum';
import { openCrisp } from '@/helpers';
import {
  deliverySettingSelector,
  deliveryStateSelector,
  deliveryZoneErrorSelector,
  handleSaveDeliverySettings,
  handleSetDeliveryZoneError,
} from '@/redux/slice/delivery.slice';
import { handleSetSaveBar } from '@/redux/slice/savebar.slice';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  ChoiceList,
  Icon,
  InlineError,
  InlineStack,
  Tag,
  TextField,
} from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StyledPadding, StyledText } from '../styled';

const DeliveryZone = ({
  dataPostalCode,
}: {
  dataPostalCode: {
    exactCode: string[];
    partialCode: string[];
  };
}) => {
  const { t } = useTranslation('deliverySettings');

  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryState = useSelector(deliveryStateSelector);
  const postalCodeErrors = useSelector(deliveryZoneErrorSelector);
  const [postalCode, setPostalCode] = useState({
    exactPostalCode: '',
    partialPostalCode: '',
  });
  const dispatch = useDispatch();

  const expandExactPostalCodeList = (input: string[]): string[] => {
    const result: string[] = [];

    input.forEach((item) => {
      const trimmed = item.trim();

      if (/^\d+\s*:\s*\d+$/.test(trimmed)) {
        const [startStr, endStr] = trimmed.split(':').map((s) => s.trim());
        const start = Number(startStr);
        const end = Number(endStr);

        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            result.push(String(i));
          }
        }
      } else {
        result.push(trimmed);
      }
    });

    return result;
  };

  const handleCheckExactPostalCode = useCallback(
    (codes: string | string[]) => {
      const codesArray = Array.isArray(codes) ? codes : codes.split(COMMA_AND_SEMICOLON_SEPARATOR).filter(Boolean);

      const newPostalCodeErrors: Record<string, string> = {};

      if (HIDE_VALIDATE_ZIP_CODE_SHOP.includes(config.shop)) return;

      console.log(dataPostalCode.exactCode, 'exact');

      const dataPostalCodeArr = expandExactPostalCodeList(dataPostalCode.exactCode);

      codesArray.forEach((rawCode) => {
        const expanded = expandExactPostalCodeList([rawCode]);
        const hasError = expanded.some((code) => {
          const trimmedCode = code.trim();
          return (
            dataPostalCodeArr.includes(trimmedCode) ||
            dataPostalCode.partialCode.some((c) =>
              trimmedCode.startsWith(c.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '')),
            )
          );
        });

        newPostalCodeErrors[rawCode] = hasError ? t('error.dupPostalCode') : '';
      });

      dispatch(
        handleSetDeliveryZoneError({
          exact: { ...newPostalCodeErrors },
        }),
      );
    },
    [dataPostalCode.exactCode, dataPostalCode.partialCode, dispatch, t],
  );

  const handleCheckPartialPostalCode = useCallback(
    (codes: string | string[]) => {
      const codesArray = Array.isArray(codes) ? codes : codes.split(COMMA_AND_SEMICOLON_SEPARATOR).filter(Boolean);
      // const codesArray = Array.isArray(codes) ? codes : codes.split(DELIMITER_OR_WHITESPACE_SEPARATOR).filter(Boolean);

      const dataPostalCodeArr = expandExactPostalCodeList(dataPostalCode.exactCode);
      const newPostalCodeErrors: Record<string, string> = {};

      if (HIDE_VALIDATE_ZIP_CODE_SHOP.includes(config.shop)) {
        return;
      }

      codesArray.forEach((rawCode) => {
        const expanded = expandExactPostalCodeList([rawCode]);

        const hasError = expanded.some((code) => {
          const currentCode = code.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '');

          const isPartialConflict = dataPostalCode.partialCode.some((c) => {
            const pc = c.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '');
            if (deliverySettings.postalCodeValidation.ukPostcodeValidation) {
              return pc === currentCode;
            }
            return currentCode.startsWith(pc) || pc.startsWith(currentCode);
          });

          return isPartialConflict || dataPostalCodeArr.includes(currentCode);
        });

        newPostalCodeErrors[rawCode] = hasError ? t('error.dupPostalCode') : '';
      });

      dispatch(
        handleSetDeliveryZoneError({
          partial: { ...newPostalCodeErrors },
        }),
      );
    },
    [
      dataPostalCode.exactCode,
      dataPostalCode.partialCode,
      deliverySettings.postalCodeValidation.ukPostcodeValidation,
      dispatch,
      t,
    ],
  );

  // Handle change choice delivery zone
  const handleChoiceDeliveryZone = (value: string[]) => {
    dispatch(
      handleSetSaveBar({
        isCheckSave: false,
        isShowSaveBar: true,
      }),
    );

    // Save settings
    dispatch(
      handleSaveDeliverySettings({
        postalCodeValidation: {
          ...deliverySettings.postalCodeValidation,
          exactMatch: value.includes(PostalCodeType.EXACT_CODE),
        },
      }),
    );
  };
  // Handle value exact code
  const handleChangeValueExactCode = (value: string) => {
    setPostalCode((prev) => ({ ...prev, exactPostalCode: value }));
    dispatch(handleSetSaveBar({ isCheckSave: false }));
  };

  const handleAddExactCode = () => {
    if (postalCode.exactPostalCode.trim()) {
      dispatch(
        handleSetSaveBar({
          isShowSaveBar: true,
          isCheckSave: false,
        }),
      );

      const arrClearSpecialChars = postalCode.exactPostalCode
        .trim()
        .replace(TRIM_SPECIAL_CHARS, '')
        .split(COMMA_AND_SEMICOLON_SEPARATOR)
        .filter(Boolean);

      if (arrClearSpecialChars.length > 0) {
        const arrPostalCode = [...(deliverySettings?.postalCodeValidation?.exactMatchPostalCodes || [])];

        const existingExpanded = new Set(expandExactPostalCodeList(arrPostalCode));

        const validNewPostalCodes = arrClearSpecialChars.filter((code) => {
          const expandedNew = expandExactPostalCodeList([code]);
          return expandedNew.every((c) => !existingExpanded.has(c));
        });

        if (validNewPostalCodes.length > 0) {
          const updatedPostalCodes = Array.from(new Set([...arrPostalCode, ...validNewPostalCodes]));

          dispatch(
            handleSaveDeliverySettings({
              postalCodeValidation: {
                ...deliverySettings.postalCodeValidation,
                exactMatchPostalCodes: updatedPostalCodes,
              },
            }),
          );
        }

        setPostalCode((prev) => ({ ...prev, exactPostalCode: '' }));
      }
    }
  };

  const handleRemovePostalCode = (item: string, index: number) => {
    const currentCode = item.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '');
    const listPostalCode = deliverySettings.postalCodeValidation.exactMatchPostalCodes;
    dispatch(handleSetSaveBar({ isShowSaveBar: true }));

    const newListTags = listPostalCode.filter((_, idx) => idx !== index);
    dispatch(
      handleSetDeliveryZoneError({
        exact: { [currentCode]: '' },
      }),
    );

    dispatch(
      handleSaveDeliverySettings({
        postalCodeValidation: {
          ...deliverySettings.postalCodeValidation,
          exactMatchPostalCodes: [...newListTags],
        },
      }),
    );
  };
  // Handle value partial code
  const handleChangeValuePartialCode = (value: string) => {
    setPostalCode((prev) => ({ ...prev, partialPostalCode: value }));

    dispatch(handleSetSaveBar({ isCheckSave: false }));
  };
  const handleAddPartialCode = () => {
    if (postalCode.partialPostalCode.trim()) {
      dispatch(handleSetSaveBar({ isShowSaveBar: true }));
      const arrPartialCode = [...(deliverySettings?.postalCodeValidation?.partialMatchPostalCodes || [])];

      // const specialChars = DELIMITER_OR_WHITESPACE_SEPARATOR;
      const arrClearSpecialChars = postalCode.partialPostalCode
        .trim()
        .replace(TRIM_SPECIAL_CHARS, '')
        .split(COMMA_AND_SEMICOLON_SEPARATOR)
        .filter(Boolean);

      if (arrClearSpecialChars.length > 0) {
        arrPartialCode.push(...arrClearSpecialChars);
      }

      const updatedPartialCodes = Array.from(new Set([...arrPartialCode, ...arrClearSpecialChars]));

      dispatch(
        handleSaveDeliverySettings({
          postalCodeValidation: {
            ...deliverySettings.postalCodeValidation,
            partialMatchPostalCodes: updatedPartialCodes,
          },
        }),
      );

      setPostalCode((prev) => ({ ...prev, partialPostalCode: '' }));
    }
  };

  const handleRemovePartialCode = (item: string, index: number) => {
    const currentCode = item.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '');
    const listPartialCode = deliverySettings.postalCodeValidation.partialMatchPostalCodes;
    dispatch(
      handleSetSaveBar({
        isShowSaveBar: true,
        isCheckSave: false,
      }),
    );
    dispatch(
      handleSetDeliveryZoneError({
        partial: { [currentCode]: '' },
      }),
    );
    const newListTags = listPartialCode.filter((_, idx) => idx !== index);

    dispatch(
      handleSaveDeliverySettings({
        postalCodeValidation: {
          ...deliverySettings.postalCodeValidation,
          partialMatchPostalCodes: [...newListTags],
        },
      }),
    );
  };

  // Handle check UK postal code
  const handleCheckUkPostCode = (checked: boolean) => {
    dispatch(handleSetSaveBar({ isShowSaveBar: true }));

    dispatch(
      handleSaveDeliverySettings({
        postalCodeValidation: {
          ...deliverySettings.postalCodeValidation,
          ukPostcodeValidation: checked,
        },
      }),
    );
  };

  useEffect(() => {
    const arrPartialCode = [...(deliverySettings?.postalCodeValidation?.partialMatchPostalCodes || [])];
    handleCheckPartialPostalCode(arrPartialCode);
  }, [deliverySettings?.postalCodeValidation?.partialMatchPostalCodes, handleCheckPartialPostalCode]);

  useEffect(() => {
    const arrExactCode = [...(deliverySettings?.postalCodeValidation?.exactMatchPostalCodes || [])];
    handleCheckExactPostalCode(arrExactCode);
  }, [
    deliverySettings?.postalCodeValidation?.exactMatchPostalCodes,
    deliverySettings?.postalCodeValidation.partialMatchPostalCodes,
    handleCheckExactPostalCode,
  ]);

  const renderTextFieldExactCode = () => (
    <Box paddingBlock={deliverySettings?.postalCodeValidation?.exactMatch ? '200' : '0'}>
      {deliverySettings?.postalCodeValidation?.exactMatch && (
        <BlockStack gap="100">
          <div
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddExactCode();
              }
            }}
          >
            <TextField
              id="exactCode"
              label={t('sections.deliveryZone.exactPostal.label')}
              placeholder={t('sections.deliveryZone.exactPostal.placeHolder')}
              value={postalCode.exactPostalCode}
              onChange={handleChangeValueExactCode}
              onBlur={() => handleChangeValueExactCode(postalCode.exactPostalCode.trim())}
              autoComplete="off"
              error={
                deliveryState.isSaving &&
                deliverySettings.isActive &&
                deliverySettings?.postalCodeValidation?.exactMatch &&
                !deliverySettings.postalCodeValidation.exactMatchPostalCodes.length
              }
              disabled={!deliverySettings?.postalCodeValidation?.exactMatch}
              connectedRight={
                <Button
                  onClick={handleAddExactCode}
                  disabled={!deliverySettings?.postalCodeValidation?.exactMatch || !postalCode.exactPostalCode.trim()}
                >
                  {t('form.add')}
                </Button>
              }
              helpText={
                deliverySettings?.postalCodeValidation?.exactMatchPostalCodes?.length > 0 && (
                  <InlineStack gap={'100'}>
                    {deliverySettings?.postalCodeValidation?.exactMatchPostalCodes?.map((item, index) => (
                      <Tag key={`tag--${index}`} onRemove={() => handleRemovePostalCode(item, index)}>
                        <InlineStack gap="050">
                          {deliveryState.isSaving &&
                            deliverySettings.isActive &&
                            item in postalCodeErrors.exact &&
                            postalCodeErrors.exact[item] && <Icon source={AlertTriangleIcon} tone="warning" />}
                          {item}
                        </InlineStack>
                      </Tag>
                    ))}
                  </InlineStack>
                )
              }
            />
          </div>
          {deliveryState.isSaving &&
            deliverySettings.isActive &&
            deliverySettings?.postalCodeValidation?.exactMatch &&
            postalCodeErrors.exact &&
            Object.values(postalCodeErrors.exact).some((item) => !!item) && (
              <InlineError fieldID="exactCode" message={t('error.dupPostalCode')} />
            )}
          {deliveryState.isSaving &&
            deliverySettings.isActive &&
            deliverySettings?.postalCodeValidation?.exactMatch &&
            !deliverySettings.postalCodeValidation.exactMatchPostalCodes.length && (
              <InlineError fieldID="" message={t('error.exactPostal')} />
            )}
        </BlockStack>
      )}
    </Box>
  );
  const renderTextFieldPartial = () => (
    <Box paddingBlock={!deliverySettings?.postalCodeValidation?.exactMatch ? '200' : '0'}>
      {!deliverySettings?.postalCodeValidation?.exactMatch && (
        <BlockStack gap={'100'}>
          <div
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddPartialCode();
              }
            }}
          >
            <TextField
              id="partialCode"
              label={t('sections.deliveryZone.partialPostal.label')}
              labelHidden
              placeholder={t('sections.deliveryZone.partialPostal.placeHolder')}
              value={postalCode.partialPostalCode}
              onChange={handleChangeValuePartialCode}
              onBlur={() => handleChangeValuePartialCode(postalCode.partialPostalCode.trim())}
              autoComplete="off"
              error={
                deliveryState.isSaving &&
                deliverySettings.isActive &&
                !deliverySettings?.postalCodeValidation?.exactMatch &&
                !deliverySettings.postalCodeValidation.partialMatchPostalCodes.length
              }
              disabled={deliverySettings?.postalCodeValidation?.exactMatch}
              connectedRight={
                <Button
                  onClick={handleAddPartialCode}
                  disabled={deliverySettings?.postalCodeValidation?.exactMatch || !postalCode.partialPostalCode.trim()}
                >
                  {t('form.add')}
                </Button>
              }
              helpText={
                deliverySettings?.postalCodeValidation?.partialMatchPostalCodes.length > 0 && (
                  <InlineStack gap={'100'}>
                    {deliverySettings?.postalCodeValidation?.partialMatchPostalCodes.map((item, index) => (
                      <Tag key={`tag--${index}`} onRemove={() => handleRemovePartialCode(item, index)}>
                        <InlineStack gap="050">
                          {deliveryState.isSaving &&
                            deliverySettings.isActive &&
                            item.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '') in postalCodeErrors.partial &&
                            postalCodeErrors.partial[item.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '')] && (
                              <Icon source={AlertTriangleIcon} tone="warning" />
                            )}
                          <span>{item}</span>
                        </InlineStack>
                      </Tag>
                    ))}
                  </InlineStack>
                )
              }
            />
          </div>
          {deliveryState.isSaving &&
            deliverySettings.isActive &&
            !deliverySettings?.postalCodeValidation?.exactMatch &&
            postalCodeErrors.partial &&
            Object.values(postalCodeErrors.partial).some((item) => !!item) && (
              <InlineError fieldID="partialCode" message={t('error.dupPostalCode')} />
            )}
          {deliveryState.isSaving &&
            deliverySettings.isActive &&
            !deliverySettings?.postalCodeValidation?.exactMatch &&
            !deliverySettings.postalCodeValidation.partialMatchPostalCodes.length && (
              <InlineError fieldID="" message={t('error.exactPostal')} />
            )}
        </BlockStack>
      )}
    </Box>
  );

  return (
    <div id="postal-code">
      <LayoutSettings
        title={t('sections.deliveryZone.title')}
        helpText={t('sections.deliveryZone.helpText')}
        children={
          <BlockStack gap={'300'}>
            <Card>
              <BlockStack gap={'050'}>
                <ChoiceList
                  title=""
                  choices={[
                    {
                      label: t('sections.deliveryZone.exactPostal.title'),
                      value: PostalCodeType.EXACT_CODE,
                    },
                    {
                      label: t('sections.deliveryZone.partialPostal.title'),
                      value: PostalCodeType.PARTIAL_POSTAL_CODE,
                      helpText: t('sections.deliveryZone.partialPostal.label'),
                    },
                  ]}
                  selected={
                    deliverySettings?.postalCodeValidation?.exactMatch
                      ? [PostalCodeType.EXACT_CODE]
                      : [PostalCodeType.PARTIAL_POSTAL_CODE]
                  }
                  onChange={handleChoiceDeliveryZone}
                />
                {!deliverySettings?.postalCodeValidation?.exactMatch && (
                  <StyledPadding paddingLeft="28px">
                    <Checkbox
                      label={t('sections.deliveryZone.ukPostalCode.title')}
                      checked={deliverySettings?.postalCodeValidation?.ukPostcodeValidation}
                      onChange={handleCheckUkPostCode}
                      helpText={t('sections.deliveryZone.ukPostalCode.helpText')}
                    />
                  </StyledPadding>
                )}
              </BlockStack>
            </Card>
            <Collapsible
              title={
                <Box>
                  <InlineStack gap={'100'}>
                    <BoldText>{t('sections.deliveryZone.zipCode.title')}</BoldText>
                    <Badge>
                      {deliverySettings.postalCodeValidation.exactMatch
                        ? deliverySettings?.postalCodeValidation?.exactMatchPostalCodes?.length.toString()
                        : deliverySettings?.postalCodeValidation?.partialMatchPostalCodes?.length.toString()}
                    </Badge>
                  </InlineStack>
                  <Box color="text-secondary">
                    <RegularText fontWeight="regular">
                      Enter numeric zipcodes separated by comma (,)
                      {deliverySettings.postalCodeValidation.exactMatch ? ' or add range by colon (:)' : ''}. Have a lot to
                      import?{' '}
                      <StyledText
                        textDecoration="underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCrisp();
                        }}
                      >
                        Contact support
                      </StyledText>
                      .
                    </RegularText>
                  </Box>
                </Box>
              }
            >
              <>
                {renderTextFieldExactCode()}
                {renderTextFieldPartial()}
              </>
            </Collapsible>
          </BlockStack>
        }
      />
    </div>
  );
};

export default DeliveryZone;
